// NovaGlowLanding.jsx — Nova Skin Care (v4) — dark editorial luxury, inspired by
// Awwwards skincare sites (Duyu Care). Deep green-charcoal palette, big editorial
// serif type, products spotlit on glowing light "podiums" (photo white-bg removed
// via mix-blend multiply on the light disc), refined reveals + marquee, preloader.
// Normal flow (no scroll-jacking). Real products + 4K images. Reduced-motion safe.
import { useEffect, useState } from "react";
import { supabase } from "./supabase";

const HI = (u) => !u ? u : (u.includes("cdn.shopify.com") ? u + (u.includes("?") ? "&" : "?") + "width=2000" : u);
const INGREDIENTS = ["Hyaluronic Acid", "Vitamin C", "Peptides", "Niacinamide", "Ceramides"];
const RITUAL = [
  { label: "Cleanse",  pick: ["prelude", "cleans"],             copy: "Gently refreshes & preps the skin." },
  { label: "Brighten", pick: ["brightening", "vivid", "genex"], copy: "Targets dullness & uneven tone." },
  { label: "Smooth",   pick: ["restorative ha", "renew", "retax"], copy: "Plumps, smooths & hydrates." },
  { label: "Glow",     pick: ["hyacyn", "mist", "eye"],          copy: "A radiant, all-day finish." },
];
const REVIEWS = [
  { t: "My skin feels smoother and more hydrated.", n: "Verified Client" },
  { t: "Finally a routine that feels luxury but simple.", n: "Verified Client" },
  { t: "The glow is real.", n: "Verified Client" },
];
const BADGES = ["Medical Spa Developed", "Derm-Inspired Routine", "Clean Daily Essentials", "Fast Shipping", "Secure Checkout"];

// Product spotlit on a glowing light podium — multiply removes the photo's white bg.
function Podium({ p, eager, size = "md" }) {
  if (!p) return null;
  return (
    <div className={"ng-podium ng-podium-" + size}>
      <span className="ng-podium-disc" />
      <img src={HI(p.image_url)} alt={p.name} loading={eager ? "eager" : "lazy"} decoding="async" />
    </div>
  );
}

export default function NovaGlowLanding() {
  const [products, setProducts] = useState([]);
  const [pre, setPre] = useState(true);
  const go = (path) => { window.location.href = path; };
  const scrollToShop = () => document.getElementById("ng-shop")?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    supabase.from("store_products").select("*").eq("active", true).not("image_url", "is", null)
      .order("order_index").then(({ data }) => setProducts((data || []).filter(p => p.image_url && !/avinichi/i.test(p.name))));
    const t = setTimeout(() => setPre(false), 1100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver((es) => es.forEach(e => { if (e.isIntersecting) e.target.classList.add("ng-in"); }), { threshold: 0.13 });
    document.querySelectorAll(".ng-reveal").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [products]);

  const pick = (keys) => products.find(p => keys.some(k => p.name.toLowerCase().includes(k)));
  const ritual = RITUAL.map((r, i) => ({ ...r, product: pick(r.pick) || products[i] })).filter(r => r.product);
  const hero = pick(["brightening", "renew", "serum"]) || products[0];
  const cream = pick(["restorative ha", "retax", "renew"]) || products[2];

  return (
    <div className="ng-root">
      <style>{CSS}</style>
      <div className={"ng-pre" + (pre ? "" : " gone")}><span className="ng-pre-mark">NOVA</span><span className="ng-pre-line" /></div>

      <header className="ng-nav">
        <div className="ng-wordmark">NOVA <span>Skin Care</span></div>
        <button className="ng-nav-cta" onClick={scrollToShop}>Shop</button>
      </header>

      {/* HERO */}
      <section className="ng-hero">
        <div className="ng-hero-glow" />
        <div className="ng-hero-grid">
          <div className="ng-hero-copy">
            <div className="ng-eyebrow">Nova Med Spa · Medical-Grade Skincare</div>
            <h1 className="ng-h1">The Nova<br/>Glow System™</h1>
            <p className="ng-sub">Luxury glow. Real results. A clean daily ritual, developed in a medical spa.</p>
            <div className="ng-cta-row">
              <button className="ng-btn ng-btn-gold" onClick={scrollToShop}>Shop the System</button>
              <button className="ng-btn ng-btn-ghost" onClick={() => go("/book")}>Take Skin Quiz</button>
            </div>
            <div className="ng-ing-row">{INGREDIENTS.map(i => <span key={i} className="ng-ing">{i}</span>)}</div>
          </div>
          <div className="ng-hero-product"><Podium p={hero} eager size="xl" /></div>
        </div>
        <div className="ng-scroll-hint">Scroll</div>
      </section>

      {/* MARQUEE */}
      <div className="ng-marquee"><div className="ng-marquee-track">{Array.from({length:2}).map((_,k)=>(
        <span key={k}>Hydrate <i>✦</i> Brighten <i>✦</i> Smooth <i>✦</i> Glow <i>✦</i> Hydrate <i>✦</i> Brighten <i>✦</i> Smooth <i>✦</i> Glow <i>✦</i> </span>
      ))}</div></div>

      {/* RITUAL */}
      <section className="ng-section">
        <div className="ng-reveal ng-head">
          <div className="ng-eyebrow">The Daily Ritual</div>
          <h2 className="ng-h2">Four steps to your glow.</h2>
        </div>
        <div className="ng-ritual">
          {ritual.map((r, i) => (
            <div key={i} className="ng-ritual-card ng-reveal" style={{ transitionDelay: `${i * 70}ms` }}>
              <div className="ng-ritual-top"><span className="ng-step">0{i + 1}</span><span className="ng-ritual-label">{r.label}</span></div>
              <Podium p={r.product} size="md" />
              <h3 className="ng-ritual-name">{r.product.name}</h3>
              <p className="ng-ritual-copy">{r.copy}</p>
              <div className="ng-ritual-foot"><span className="ng-price">${Number(r.product.price).toFixed(0)}</span>
                <button className="ng-btn ng-btn-mini" onClick={() => go("/shop")}>Add</button></div>
            </div>
          ))}
        </div>
      </section>

      {/* TEXTURE STATEMENT */}
      <section className="ng-texture">
        <div className="ng-texture-copy ng-reveal">
          <div className="ng-eyebrow">The Feel</div>
          <h2 className="ng-h2 big">Rich.<br/>Silky.<br/><span className="ng-accent">Absorbs in seconds.</span></h2>
          <p className="ng-sub">A cushiony, fast-absorbing texture that melts into skin — never greasy, always luxurious.</p>
          {cream && <button className="ng-btn ng-btn-gold" onClick={() => go("/shop")}>Shop {cream.name}</button>}
        </div>
        <div className="ng-texture-visual ng-reveal">
          <span className="ng-smear" /><span className="ng-dab" />
          <Podium p={cream} size="lg" />
        </div>
      </section>

      {/* SHOP */}
      <section className="ng-section" id="ng-shop">
        <div className="ng-reveal ng-head"><div className="ng-eyebrow">The Collection</div><h2 className="ng-h2">Shop Nova Skin Care.</h2></div>
        <div className="ng-grid">
          {products.map((p, i) => (
            <div key={p.id} className="ng-card ng-reveal" style={{ transitionDelay: `${(i % 4) * 50}ms` }}>
              <Podium p={p} size="card" />
              <div className="ng-card-body">
                {p.brand && <div className="ng-card-brand">{p.brand}</div>}
                <div className="ng-stars">★★★★★</div>
                <h3 className="ng-card-name">{p.name}</h3>
                <div className="ng-card-foot"><span className="ng-price">${Number(p.price).toFixed(0)}</span>
                  <button className="ng-btn ng-btn-mini" onClick={() => go("/shop")}>Add to Cart</button></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BUNDLE */}
      <section className="ng-bundle">
        <div className="ng-bundle-inner ng-reveal">
          <div className="ng-eyebrow center">Complete Routine</div>
          <h2 className="ng-h2 center">The Nova Glow System™</h2>
          <p className="ng-sub center" style={{maxWidth:520,margin:"0 auto"}}>Your complete daily routine for a smoother, brighter, more radiant-looking complexion.</p>
          <div className="ng-bundle-tray">{ritual.map((r, i) => <div key={i} className="ng-bundle-item"><Podium p={r.product} size="sm" /></div>)}</div>
          <button className="ng-btn ng-btn-gold lg" onClick={() => go("/shop")}>Shop the Complete System</button>
          <div className="ng-bundle-offer">✦ Limited launch — free Hydration Mask with the System</div>
        </div>
      </section>

      {/* BADGES */}
      <section className="ng-badges">{BADGES.map(b => <div key={b} className="ng-badge ng-reveal"><span>✦</span>{b}</div>)}</section>

      {/* REVIEWS */}
      <section className="ng-section">
        <div className="ng-reveal ng-head"><div className="ng-eyebrow">Loved by Our Clients</div><h2 className="ng-h2">The glow speaks for itself.</h2></div>
        <div className="ng-reviews">{REVIEWS.map((r, i) => (
          <div key={i} className="ng-review ng-reveal" style={{ transitionDelay: `${i * 70}ms` }}>
            <div className="ng-stars">★★★★★</div><p className="ng-review-t">“{r.t}”</p><div className="ng-review-n">— {r.n}</div>
          </div>
        ))}</div>
      </section>

      {/* FINAL */}
      <section className="ng-final">
        <div className="ng-hero-glow" />
        <div className="ng-reveal">
          <h2 className="ng-h1 sm center">Start your<br/>Nova Glow routine.</h2>
          <div className="ng-cta-row center">
            <button className="ng-btn ng-btn-gold lg" onClick={() => go("/shop")}>Shop Now</button>
            <button className="ng-btn ng-btn-ghost lg" onClick={() => go("/book")}>Book a Skin Consultation</button>
          </div>
        </div>
        <footer className="ng-footer">Nova Med Spa · 1451 Old Country Rd, Plainview NY 11803 · <a href="https://www.novamedicalspa.com">novamedicalspa.com</a></footer>
      </section>

      <div className="ng-sticky">
        <button className="ng-btn ng-btn-gold" onClick={() => go("/shop")}>Shop the System</button>
        <button className="ng-btn ng-btn-ghost" onClick={() => go("/book")}>Skin Quiz</button>
      </div>
    </div>
  );
}

const CSS = `
.ng-root{--bg:#20261f;--bg2:#272f26;--panel:#2c352b;--panel2:#323c30;--cream:#f1ece1;--stone:#c9c1b0;--sage:#9aa893;--gold:#c8a86a;--goldL:#e6cf95;--line:rgba(241,236,225,.12);--text:#ece7da;
  font-family:'Manrope','Montserrat',system-ui,sans-serif;color:var(--text);background:var(--bg);overflow-x:hidden;-webkit-font-smoothing:antialiased;}
.ng-root *{box-sizing:border-box;margin:0;}
.ng-root h1,.ng-root h2,.ng-root h3{font-family:'Fraunces','Playfair Display',Georgia,serif;font-weight:400;letter-spacing:-0.015em;color:var(--cream);}
.ng-eyebrow{font-size:11.5px;letter-spacing:.34em;text-transform:uppercase;font-weight:700;color:var(--sage);}
.ng-eyebrow.center{text-align:center;}
.ng-h1{font-size:clamp(46px,8vw,104px);line-height:.98;}
.ng-h1.sm{font-size:clamp(38px,6vw,72px);}
.ng-h2{font-size:clamp(30px,5vw,60px);line-height:1.04;}
.ng-h2.center{text-align:center;}
.ng-h2.big{font-size:clamp(42px,7vw,92px);line-height:.98;}
.ng-accent{color:var(--gold);font-style:italic;}
.ng-sub{font-size:clamp(15px,1.5vw,18px);color:var(--stone);line-height:1.6;max-width:480px;}
.ng-sub.center{text-align:center;}
.ng-head{text-align:center;margin-bottom:48px;display:flex;flex-direction:column;gap:12px;align-items:center;}
.ng-price{font-weight:800;color:var(--cream);font-size:16px;}

.ng-btn{font-family:inherit;font-weight:700;font-size:15px;border:none;border-radius:100px;padding:14px 28px;cursor:pointer;transition:transform .2s,box-shadow .2s,background .2s,color .2s;white-space:nowrap;}
.ng-btn:hover{transform:translateY(-2px);}
.ng-btn.lg{padding:17px 38px;font-size:16px;}
.ng-btn-gold{background:linear-gradient(135deg,var(--goldL),var(--gold));color:#241c0c;box-shadow:0 12px 32px rgba(200,168,106,.28);}
.ng-btn-gold:hover{box-shadow:0 18px 44px rgba(200,168,106,.42);}
.ng-btn-ghost{background:transparent;color:var(--cream);border:1.5px solid rgba(241,236,225,.28);}
.ng-btn-ghost:hover{border-color:var(--gold);color:var(--gold);}
.ng-btn-mini{background:transparent;color:var(--cream);border:1.5px solid rgba(241,236,225,.28);padding:9px 16px;font-size:12.5px;}
.ng-btn-mini:hover{background:var(--gold);color:#241c0c;border-color:var(--gold);}
.ng-cta-row{display:flex;gap:14px;flex-wrap:wrap;margin-top:28px;}
.ng-cta-row.center{justify-content:center;}

.ng-reveal{opacity:0;transform:translateY(30px);transition:opacity .85s cubic-bezier(.16,.8,.3,1),transform .85s cubic-bezier(.16,.8,.3,1);}
.ng-reveal.ng-in{opacity:1;transform:none;}

/* preloader */
.ng-pre{position:fixed;inset:0;z-index:200;background:var(--bg);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;transition:opacity .7s,visibility .7s;}
.ng-pre.gone{opacity:0;visibility:hidden;}
.ng-pre-mark{font-family:'Fraunces',serif;font-size:34px;letter-spacing:.42em;color:var(--cream);padding-left:.42em;animation:ng-fadein .8s ease;}
.ng-pre-line{width:120px;height:2px;background:linear-gradient(90deg,transparent,var(--gold),transparent);animation:ng-grow 1s ease;}

/* ★ PRODUCT PODIUM — light disc on dark; photo white-bg blends away via multiply */
.ng-podium{position:relative;display:flex;align-items:center;justify-content:center;}
.ng-podium-disc{position:absolute;inset:6% 6% 10% 6%;border-radius:50%;background:radial-gradient(circle at 50% 38%,#fcf8ef,#efe6d3 58%,#e3d8c1 100%);box-shadow:0 40px 80px rgba(0,0,0,.45),inset 0 2px 30px rgba(255,255,255,.6);}
.ng-podium img{position:relative;z-index:2;max-width:78%;max-height:84%;object-fit:contain;mix-blend-mode:multiply;}
.ng-podium-xl{width:min(86vw,460px);aspect-ratio:1/1;}
.ng-podium-lg{width:min(80vw,400px);aspect-ratio:1/1;}
.ng-podium-md{width:100%;aspect-ratio:1/1.05;}
.ng-podium-sm{width:96px;height:128px;}
.ng-podium-sm .ng-podium-disc{inset:-14% -6% -6% -6%;}
.ng-podium-card{width:100%;aspect-ratio:1/1;}

.ng-nav{position:sticky;top:0;z-index:60;display:flex;align-items:center;justify-content:space-between;padding:16px 26px;background:rgba(32,38,31,.6);backdrop-filter:blur(16px);border-bottom:1px solid var(--line);}
.ng-wordmark{font-family:'Fraunces',serif;font-size:21px;letter-spacing:.05em;color:var(--cream);}
.ng-wordmark span{color:var(--gold);}
.ng-nav-cta{background:none;border:1px solid rgba(241,236,225,.25);border-radius:100px;padding:8px 20px;font-family:inherit;font-weight:700;font-size:13px;cursor:pointer;color:var(--cream);}

/* HERO */
.ng-hero{position:relative;min-height:94vh;display:flex;flex-direction:column;justify-content:center;padding:30px 26px 60px;background:radial-gradient(100% 80% at 72% 40%,var(--bg2),var(--bg) 60%);overflow:hidden;}
.ng-hero-glow{position:absolute;right:14%;top:46%;transform:translate(50%,-50%);width:min(80vw,680px);height:min(80vw,680px);border-radius:50%;background:radial-gradient(circle,rgba(200,168,106,.22),rgba(154,168,147,.12) 40%,transparent 66%);filter:blur(20px);animation:ng-breathe 9s ease-in-out infinite;}
.ng-hero-grid{position:relative;z-index:3;display:grid;grid-template-columns:1.08fr .92fr;gap:30px;align-items:center;max-width:1140px;width:100%;margin:0 auto;}
.ng-hero-copy .ng-h1{margin:14px 0 18px;}
.ng-ing-row{display:flex;flex-wrap:wrap;gap:8px;margin-top:30px;}
.ng-ing{font-size:11.5px;font-weight:600;color:var(--stone);border:1px solid var(--line);padding:6px 13px;border-radius:100px;}
.ng-hero-product{display:flex;align-items:center;justify-content:center;}
.ng-scroll-hint{position:absolute;bottom:24px;left:50%;transform:translateX(-50%);font-size:10.5px;letter-spacing:.26em;text-transform:uppercase;color:var(--sage);animation:ng-bob 2s ease-in-out infinite;}

/* MARQUEE */
.ng-marquee{background:var(--gold);color:#241c0c;overflow:hidden;white-space:nowrap;padding:14px 0;}
.ng-marquee-track{display:inline-block;animation:ng-scroll 24s linear infinite;font-family:'Fraunces',serif;font-size:clamp(20px,3vw,32px);font-style:italic;}
.ng-marquee-track i{font-style:normal;opacity:.55;margin:0 .25em;}

/* SECTIONS */
.ng-section{max-width:1180px;margin:0 auto;padding:clamp(64px,9vw,120px) 26px;}

/* RITUAL */
.ng-ritual{display:grid;grid-template-columns:repeat(4,1fr);gap:18px;}
.ng-ritual-card{background:linear-gradient(180deg,var(--panel),var(--panel2));border:1px solid var(--line);border-radius:22px;padding:20px;display:flex;flex-direction:column;transition:transform .3s,border-color .3s;}
.ng-ritual-card:hover{transform:translateY(-6px);border-color:rgba(200,168,106,.4);}
.ng-ritual-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;}
.ng-step{font-family:'Fraunces',serif;font-size:15px;color:var(--gold);}
.ng-ritual-label{font-size:10.5px;letter-spacing:.2em;text-transform:uppercase;color:var(--sage);font-weight:700;}
.ng-ritual-name{font-size:17px;margin:14px 0 4px;line-height:1.2;}
.ng-ritual-copy{font-size:12.5px;color:var(--stone);line-height:1.5;flex:1;}
.ng-ritual-foot{display:flex;align-items:center;justify-content:space-between;margin-top:16px;}

/* TEXTURE */
.ng-texture{display:grid;grid-template-columns:1fr 1fr;align-items:center;gap:30px;max-width:1180px;margin:0 auto;padding:clamp(50px,7vw,100px) 26px;}
.ng-texture-copy{display:flex;flex-direction:column;gap:16px;align-items:flex-start;}
.ng-texture-visual{position:relative;min-height:46vh;display:flex;align-items:center;justify-content:center;}
.ng-smear{position:absolute;left:-4%;top:50%;width:96%;height:280px;transform:translateY(-50%) rotate(-7deg);border-radius:50% 50% 48% 52%/60% 58% 42% 40%;background:radial-gradient(60% 70% at 40% 42%,rgba(252,248,239,.95),rgba(231,216,184,.6) 55%,rgba(231,216,184,0) 100%);filter:blur(2px);}
.ng-dab{position:absolute;left:12%;top:30%;width:90px;height:90px;border-radius:54% 46% 50% 50%/52% 48% 52% 48%;background:radial-gradient(circle at 38% 34%,#fff,#efdcbb 70%);box-shadow:inset -6px -8px 16px rgba(150,120,60,.3);}

/* GRID */
.ng-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(238px,1fr));gap:20px;}
.ng-card{background:linear-gradient(180deg,var(--panel),var(--panel2));border:1px solid var(--line);border-radius:22px;overflow:hidden;display:flex;flex-direction:column;transition:transform .3s,border-color .3s;}
.ng-card:hover{transform:translateY(-6px);border-color:rgba(200,168,106,.4);}
.ng-card .ng-podium{padding:8px 8px 0;}
.ng-card-body{padding:6px 18px 18px;display:flex;flex-direction:column;flex:1;}
.ng-card-brand{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--sage);font-weight:700;margin-bottom:5px;}
.ng-stars{color:var(--gold);font-size:13px;letter-spacing:2px;margin-bottom:5px;}
.ng-card-name{font-size:16px;line-height:1.2;flex:1;}
.ng-card-foot{display:flex;align-items:center;justify-content:space-between;margin-top:14px;gap:10px;}

/* BUNDLE */
.ng-bundle{background:radial-gradient(110% 90% at 50% 0%,var(--bg2),var(--bg));padding:clamp(64px,9vw,120px) 26px;text-align:center;}
.ng-bundle-inner{max-width:880px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:16px;}
.ng-bundle-tray{display:flex;gap:14px;justify-content:center;flex-wrap:wrap;align-items:flex-end;margin:18px 0 8px;padding:26px 22px 18px;background:linear-gradient(180deg,rgba(241,236,225,.06),rgba(241,236,225,.02));border:1px solid var(--line);border-radius:24px;}
.ng-bundle-offer{font-size:12.5px;color:var(--sage);letter-spacing:.05em;margin-top:6px;}

/* BADGES */
.ng-badges{display:flex;flex-wrap:wrap;justify-content:center;gap:12px;padding:44px 22px;border-top:1px solid var(--line);border-bottom:1px solid var(--line);}
.ng-badge{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:var(--cream);padding:10px 18px;}
.ng-badge span{color:var(--gold);}

/* REVIEWS */
.ng-reviews{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:18px;}
.ng-review{background:rgba(241,236,225,.04);border:1px solid var(--line);border-radius:22px;padding:28px 26px;}
.ng-review-t{font-family:'Fraunces',serif;font-size:21px;line-height:1.4;color:var(--cream);margin-top:12px;}
.ng-review-n{font-size:12px;color:var(--sage);margin-top:14px;letter-spacing:.06em;text-transform:uppercase;font-weight:700;}

/* FINAL */
.ng-final{position:relative;text-align:center;padding:clamp(80px,12vw,150px) 26px 40px;overflow:hidden;}
.ng-footer{position:relative;z-index:2;margin-top:64px;font-size:12.5px;color:var(--sage);}
.ng-footer a{color:var(--gold);text-decoration:none;}
.ng-sticky{display:none;}

@media(max-width:860px){
  .ng-hero{min-height:auto;padding-top:20px;}
  .ng-hero-grid{grid-template-columns:1fr;gap:10px;text-align:center;}
  .ng-hero-copy{order:2;display:flex;flex-direction:column;align-items:center;}
  .ng-hero-product{order:1;}
  .ng-cta-row,.ng-ing-row{justify-content:center;}
  .ng-hero-glow{right:50%;top:22%;}
  .ng-ritual{grid-template-columns:repeat(2,1fr);gap:12px;}
  .ng-texture{grid-template-columns:1fr;text-align:center;}.ng-texture-copy{align-items:center;}
  .ng-texture-visual{min-height:40vh;}
  .ng-sticky{display:flex;position:fixed;bottom:0;left:0;right:0;z-index:80;gap:10px;padding:12px 14px calc(12px + env(safe-area-inset-bottom));background:rgba(32,38,31,.96);backdrop-filter:blur(14px);border-top:1px solid var(--line);}
  .ng-sticky .ng-btn{flex:1;padding:14px 8px;font-size:13px;}
  .ng-final{padding-bottom:118px;}
}
@media(max-width:460px){ .ng-ritual{grid-template-columns:1fr;} }

@keyframes ng-scroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@keyframes ng-breathe{0%,100%{transform:translate(50%,-50%) scale(1);opacity:.9}50%{transform:translate(50%,-50%) scale(1.08);opacity:1}}
@keyframes ng-bob{0%,100%{transform:translateX(-50%) translateY(0)}50%{transform:translateX(-50%) translateY(6px)}}
@keyframes ng-fadein{from{opacity:0}to{opacity:1}}
@keyframes ng-grow{from{width:0}to{width:120px}}
@media(prefers-reduced-motion:reduce){.ng-root *{animation:none!important;transition:none!important;}.ng-reveal{opacity:1!important;transform:none!important;}.ng-pre{display:none;}.ng-marquee-track{animation:none!important;}}
`;
